import { useCallback, useEffect, useMemo, useState } from 'react';

// Per-org "recently visited" + "pinned" sidebar state, persisted to
// localStorage. Mirrors the Stripe sidebar UX: pinned items stick to the
// top of the recents accordion across sessions; unpinned items roll off
// after the cap is hit. Keyed by org slug so each tenant gets its own
// list — switching orgs surfaces the right history.
//
// We deliberately keep this in localStorage (not the DB) for two reasons:
//   1. It's per-device anyway — a user on their laptop and phone naturally
//      has different "recents", and trying to sync would either thrash the
//      list or feel out of date.
//   2. Zero backend coupling. No new schema, no new endpoint, no new
//      cross-org table. We can always promote to DB later if device-sync
//      becomes a real ask.

const RECENT_LIMIT = 5;

interface State {
  recents: string[]; // nav item IDs, most-recent first
  pinned: string[]; // nav item IDs, in pin order (oldest pin first)
}

const empty: State = { recents: [], pinned: [] };

function storageKey(slug: string): string {
  return `sportadmin:nav-recents:${slug}`;
}

function read(slug: string): State {
  if (!slug) return empty;
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<State>;
    return {
      recents: Array.isArray(parsed.recents) ? parsed.recents.filter((s) => typeof s === 'string') : [],
      pinned: Array.isArray(parsed.pinned) ? parsed.pinned.filter((s) => typeof s === 'string') : [],
    };
  } catch {
    // Corrupt / cross-tab change / SSR — fall back to empty rather than
    // crashing the shell. Worst case the user sees an empty recents list
    // for one render and it repopulates as they navigate.
    return empty;
  }
}

function write(slug: string, state: State): void {
  if (!slug) return;
  try {
    window.localStorage.setItem(storageKey(slug), JSON.stringify(state));
  } catch {
    // Quota exceeded / storage disabled (private mode on some browsers).
    // We treat persistence as best-effort; the in-memory state still works
    // for the current session.
  }
}

export interface RecentNav {
  // Pinned items in pin order, then unpinned recents up to RECENT_LIMIT.
  // Caller renders this directly — order is the displayed order.
  ordered: string[];
  pinned: Set<string>;
  // Push a navigation event. De-duplicates and trims to RECENT_LIMIT.
  // Pinned items aren't subject to the limit (they always stay).
  push: (id: string) => void;
  togglePin: (id: string) => void;
  isPinned: (id: string) => boolean;
}

export function useRecentNav(slug: string): RecentNav {
  const [state, setState] = useState<State>(() => read(slug));

  // Re-read when the org changes — different org = different list.
  useEffect(() => {
    setState(read(slug));
  }, [slug]);

  // Persist on every change. We also re-emit a storage event implicitly
  // via setItem; if we ever want cross-tab sync we'd add a `storage`
  // listener here, but for now per-tab state is fine.
  useEffect(() => {
    write(slug, state);
  }, [slug, state]);

  const push = useCallback((id: string) => {
    setState((prev) => {
      // No-op when the path is already at the head — saves a write per
      // hash-change / re-render that hits the same route.
      if (prev.recents[0] === id) return prev;
      const filtered = prev.recents.filter((r) => r !== id);
      const next = [id, ...filtered].slice(0, RECENT_LIMIT);
      return { ...prev, recents: next };
    });
  }, []);

  const togglePin = useCallback((id: string) => {
    setState((prev) => {
      if (prev.pinned.includes(id)) {
        return { ...prev, pinned: prev.pinned.filter((p) => p !== id) };
      }
      // When pinning, also remove from recents — pinned items are shown
      // separately at the top, no point duplicating.
      return {
        recents: prev.recents.filter((r) => r !== id),
        pinned: [...prev.pinned, id],
      };
    });
  }, []);

  const pinnedSet = useMemo(() => new Set(state.pinned), [state.pinned]);
  const isPinned = useCallback((id: string) => pinnedSet.has(id), [pinnedSet]);

  // Compose the displayed order: pinned first (in pin order), then
  // recents (most-recent first), filtering out anything already pinned
  // so we don't render duplicates.
  const ordered = [
    ...state.pinned,
    ...state.recents.filter((r) => !pinnedSet.has(r)),
  ];

  return { ordered, pinned: pinnedSet, push, togglePin, isPinned };
}
